import { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Search } from 'lucide-react';
import { useSites, useSaveSites } from '@/hooks/useDashboard';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { HostBadge } from '@/components/HostBadge';
import { MarkerToggle } from '@/components/MarkerToggle';
import { fmtEuroExact, cn } from '@/lib/utils';
import type { Site } from '@/lib/types';
import { useIsMobile } from '@/hooks/useDevice';

type SortKey = 'domain' | 'client' | 'host' | 'status' | 'price' | 'mrr';
type Dir = 'asc' | 'desc' | null;

const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'active', label: 'Actif' },
  { value: 'pending', label: 'En attente' },
  { value: 'blocked', label: 'Bloqué' },
  { value: 'paused', label: 'En pause' },
  { value: 'archived', label: 'Archivé' },
];

function siteMrr(s: Site): number {
  const p = Number(s.price_ht ?? 0);
  if (!p) return 0;
  return s.billing === 'yearly' ? p / 12 : p;
}

export function Sites() {
  const { data, isLoading } = useSites();
  const save = useSaveSites();
  const [filter, setFilter] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('mrr');
  const [sortDir, setSortDir] = useState<Dir>('desc');
  const isMobile = useIsMobile();

  const sites = data ?? [];

  const filtered = useMemo(() => {
    const q = filter.toLowerCase().trim();
    return sites
      .filter((s) => {
        if (!q) return true;
        return [s.domain, s.client, s.client_company, s.notes].filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
      })
      .sort((a, b) => {
        if (!sortDir) return 0;
        const dir = sortDir === 'asc' ? 1 : -1;
        const av = sortKey === 'mrr' ? siteMrr(a) : sortKey === 'price' ? Number(a.price_ht ?? 0) : (a as Record<string, unknown>)[sortKey] ?? '';
        const bv = sortKey === 'mrr' ? siteMrr(b) : sortKey === 'price' ? Number(b.price_ht ?? 0) : (b as Record<string, unknown>)[sortKey] ?? '';
        if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
        return String(av).localeCompare(String(bv)) * dir;
      });
  }, [sites, filter, sortKey, sortDir]);

  const totalMrr = filtered.reduce((s, x) => s + siteMrr(x), 0);
  const activeCount = filtered.filter((s) => s.status === 'active').length;

  const updateField = (i: number, field: keyof Site, value: unknown) => {
    const next = sites.map((s) => ({ ...s }));
    const orig = filtered[i];
    const idx = sites.findIndex((s) => s.domain === orig.domain);
    if (idx === -1) return;
    (next[idx] as Record<string, unknown>)[field as string] = value;
    save.mutate(next);
  };

  const toggleSort = (k: SortKey) => {
    if (sortKey !== k) { setSortKey(k); setSortDir('desc'); return; }
    setSortDir(sortDir === 'desc' ? 'asc' : sortDir === 'asc' ? null : 'desc');
  };

  if (isLoading) return <Skeleton className="h-96" />;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filtrer par domaine, client, notes…" className="pl-8" />
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span><strong className="text-foreground num">{filtered.length}</strong> sites</span>
          <span>·</span>
          <span><strong className="text-success num">{activeCount}</strong> actifs</span>
          <span>·</span>
          <span>MRR <strong className="text-foreground num">{fmtEuroExact(totalMrr)}</strong></span>
        </div>
      </div>

      {isMobile ? (
        <div className="space-y-2">
          {filtered.map((s, i) => (
            <SiteCard key={s.domain} site={s} onUpdate={(field, v) => updateField(i, field, v)} />
          ))}
        </div>
      ) : (
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortHead k="domain" current={sortKey} dir={sortDir} onSort={toggleSort}>Domaine</SortHead>
                  <SortHead k="client" current={sortKey} dir={sortDir} onSort={toggleSort}>Client</SortHead>
                  <SortHead k="host" current={sortKey} dir={sortDir} onSort={toggleSort}>Hébergeur</SortHead>
                  <SortHead k="status" current={sortKey} dir={sortDir} onSort={toggleSort}>Statut</SortHead>
                  <SortHead k="price" current={sortKey} dir={sortDir} onSort={toggleSort} align="right">Prix HT</SortHead>
                  <SortHead k="mrr" current={sortKey} dir={sortDir} onSort={toggleSort} align="right">MRR/mois</SortHead>
                  <TableHead>Marqueurs</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((s, i) => (
                  <TableRow key={s.domain}>
                    <TableCell className="font-medium">{s.domain}</TableCell>
                    <TableCell className="text-sm">{s.client ?? s.client_company ?? '—'}</TableCell>
                    <TableCell><HostBadge host={s.host} /></TableCell>
                    <TableCell>
                      <Select value={s.status ?? 'active'} onValueChange={(v) => updateField(i, 'status', v)}>
                        <SelectTrigger className="h-8 w-32 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      <Input
                        type="number"
                        defaultValue={s.price_ht ?? ''}
                        onBlur={(e) => updateField(i, 'price_ht', e.target.value === '' ? null : Number(e.target.value))}
                        className="h-8 w-24 text-right text-sm num"
                      />
                    </TableCell>
                    <TableCell className={cn('text-right num font-semibold', siteMrr(s) > 0 ? 'text-success' : 'text-muted-foreground')}>
                      {siteMrr(s) > 0 ? fmtEuroExact(siteMrr(s)) : '—'}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        <MarkerToggle type="prospect" active={!!s.to_prospect} onToggle={() => updateField(i, 'to_prospect', !s.to_prospect)} title="À démarcher" />
                        <MarkerToggle type="migrate" active={s.migration_target === 'hostinger'} onToggle={() => updateField(i, 'migration_target', s.migration_target === 'hostinger' ? null : 'hostinger')} title="À migrer vers Hostinger" />
                        <MarkerToggle type="mutualize" active={!!s.mutualization_group} onToggle={() => updateField(i, 'mutualization_group', s.mutualization_group ? null : 'default')} title={s.mutualization_group ? `Groupe : ${s.mutualization_group}` : 'À mutualiser'} />
                        <MarkerToggle type="email" active={!!s.email_included} onToggle={() => updateField(i, 'email_included', !s.email_included)} title="Email pro inclus" />
                      </div>
                    </TableCell>
                    <TableCell className="max-w-xs">
                      <Input
                        defaultValue={s.notes ?? ''}
                        onBlur={(e) => updateField(i, 'notes', e.target.value || null)}
                        className="h-8 text-sm"
                        placeholder="…"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <p className="text-xs text-muted-foreground">
        Tous les prix sont en HT. Les annuels sont divisés par 12 pour le MRR. Modifications auto-sauvegardées.
      </p>
    </div>
  );
}

function SortHead({ k, current, dir, onSort, children, align = 'left' }: {
  k: SortKey; current: SortKey; dir: Dir; onSort: (k: SortKey) => void; children: React.ReactNode; align?: 'left' | 'right';
}) {
  const active = current === k && dir !== null;
  const Icon = !active ? ArrowUpDown : dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <TableHead>
      <button
        type="button"
        onClick={() => onSort(k)}
        className={cn(
          'inline-flex items-center gap-1 font-medium hover:text-foreground transition-colors',
          align === 'right' && 'flex-row-reverse w-full justify-start',
          active ? 'text-foreground' : 'text-muted-foreground'
        )}
      >
        {children}
        <Icon className="h-3 w-3" />
      </button>
    </TableHead>
  );
}

function SiteCard({ site, onUpdate }: { site: Site; onUpdate: (field: keyof Site, value: unknown) => void }) {
  return (
    <Card>
      <CardContent className="space-y-2 p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="font-medium truncate">{site.domain}</div>
          <HostBadge host={site.host} />
        </div>
        <div className="text-xs text-muted-foreground">{site.client ?? site.client_company ?? '—'}</div>
        <div className="flex items-center justify-between gap-2 text-sm">
          <Select value={site.status ?? 'active'} onValueChange={(v) => onUpdate('status', v)}>
            <SelectTrigger className="h-8 flex-1 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className={cn('num font-semibold shrink-0', siteMrr(site) > 0 ? 'text-success' : 'text-muted-foreground')}>
            {siteMrr(site) > 0 ? fmtEuroExact(siteMrr(site)) + '/mois' : '—'}
          </span>
        </div>
        <div className="flex flex-wrap gap-1">
          <MarkerToggle type="prospect" active={!!site.to_prospect} onToggle={() => onUpdate('to_prospect', !site.to_prospect)} />
          <MarkerToggle type="migrate" active={site.migration_target === 'hostinger'} onToggle={() => onUpdate('migration_target', site.migration_target === 'hostinger' ? null : 'hostinger')} />
          <MarkerToggle type="mutualize" active={!!site.mutualization_group} onToggle={() => onUpdate('mutualization_group', site.mutualization_group ? null : 'default')} />
          <MarkerToggle type="email" active={!!site.email_included} onToggle={() => onUpdate('email_included', !site.email_included)} />
        </div>
      </CardContent>
    </Card>
  );
}
